import { Card, Typography, Space, Tooltip } from "antd";
import { ClockCircleOutlined } from "@ant-design/icons";
import { VaultFile } from "@/types";
import { FileIcon, ShareBadge } from "@/components/shared";
import {
  formatFileSize,
  formatRelativeDate,
  formatDeletionCountdown,
  getDeletionUrgency,
} from "@/utils/formatters";

const { Text } = Typography;

interface FileCardProps {
  file: VaultFile;
  isSelected?: boolean;
  onClick?: (e: React.MouseEvent) => void;
  onDoubleClick?: () => void;
  onContextMenu?: (e: React.MouseEvent) => void;
}

export default function FileCard({
  file,
  isSelected = false,
  onClick,
  onDoubleClick,
  onContextMenu,
}: FileCardProps) {
  const isShared = file.sharedWith?.length > 0;
  const urgency = file.scheduledDeletionAt ? getDeletionUrgency(file.scheduledDeletionAt) : "normal";
  const deletionText = file.scheduledDeletionAt ? formatDeletionCountdown(file.scheduledDeletionAt) : null;

  const urgencyColors = {
    critical: "#EF4444",
    warning: "#F97316",
    normal: "#6B7280",
  };

  return (
    <Card
      className={`file-card transition-all ${isSelected ? "selected-item" : ""}`}
      hoverable
      onClick={onClick}
      onDoubleClick={onDoubleClick}
      onContextMenu={onContextMenu}
      style={{
        cursor: "pointer",
        borderRadius: 8,
        border: isSelected ? "1px solid #0F6E56" : "1px solid rgba(0, 0, 0, 0.08)",
      }}
      styles={{
        body: {
          padding: 16,
        },
      }}
    >
      {/* File icon */}
      <div style={{ marginBottom: 12 }}>
        <FileIcon mimeType={file.mimeType} size="large" />
      </div>

      {/* File name */}
      <Text
        strong
        ellipsis
        style={{
          display: "block",
          fontSize: 14,
          marginBottom: 8,
        }}
        title={file.fileName}
      >
        {file.fileName}
      </Text>

      {/* Bottom row: Metadata */}
      <Space size={8} wrap>
        <Text type="secondary" style={{ fontSize: 12 }}>
          {formatFileSize(file.size)}
        </Text>
        <Text type="secondary" style={{ fontSize: 12 }}>
          ·
        </Text>
        <Text type="secondary" style={{ fontSize: 12 }}>
          {formatRelativeDate(file.updatedAt)}
        </Text>
        {isShared && <ShareBadge />}
        {deletionText && (
          <Tooltip title="This document will be automatically deleted for security">
            <span
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: 4,
                fontSize: 11,
                color: urgencyColors[urgency],
                fontWeight: urgency === "critical" ? 600 : 400,
              }}
            >
              <ClockCircleOutlined style={{ fontSize: 10 }} />
              {deletionText}
            </span>
          </Tooltip>
        )}
      </Space>
    </Card>
  );
}
